import { BASE_API_URL } from "../config";
import { LoginRequest, ResponseLoginWeb } from "../types/auth.types";

export class AuthService {
  static async login(credentials: LoginRequest): Promise<ResponseLoginWeb> {
    try {
      console.log("Sending login request with:", credentials);

      const response = await fetch(`${BASE_API_URL}/auth/login-web`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      console.log("Response status:", response.status);

      const apiResponse = await response.json();
      console.log("Full API Response:", apiResponse);

      if (!response.ok || !apiResponse.success) {
        const error = new Error(apiResponse.message || "Login failed");
        (error as any).response = {
          data: apiResponse,
          status: response.status,
          statusText: response.statusText,
        };
        throw error;
      }

      // Extract the actual user data from the nested structure
      const rawUserData = apiResponse.data?.data;
      if (!rawUserData) {
        throw new Error("Invalid response structure");
      }

      console.log("Raw user data from API:", rawUserData);

      // Convert to ResponseLoginWeb interface
      const userData: ResponseLoginWeb = {
        jwt: rawUserData.jwt,
        refreshToken: rawUserData.refreshToken,
        id: rawUserData.id,
        userName: rawUserData.userName,
        fullName: rawUserData.fullName,
        email: rawUserData.email,
        phone: rawUserData.phone,
        dateExpires: rawUserData.dateExpires,
        departmentName: rawUserData.departmentName,
        env: rawUserData.env,
        empId: rawUserData.empId,
        clientId: rawUserData.clientId,
        tblDmEmployee: rawUserData.tblDmEmployee,
      };

      console.log("Converted user data:", userData);
      console.log("JWT Token:", userData.jwt); // Debug log for token

      return userData;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  }

  static setToken(token: string): void {
    localStorage.setItem("authToken", token);
  }

  static setUserData(userData: ResponseLoginWeb): void {
    console.log("=== DEBUG setUserData ===");
    console.log("Full userData object:", userData);
    console.log("userData.jwt type:", typeof userData.jwt);
    console.log("userData.jwt value:", userData.jwt);
    console.log("userData.jwt length:", userData.jwt?.length);

    try {
      // Kiểm tra token có tồn tại không
      if (!userData.jwt || userData.jwt.trim() === "") {
        console.error("JWT token is empty or null!");
        console.error("userData keys:", Object.keys(userData));
        throw new Error("JWT token is missing");
      }

      // Force convert to string nếu cần
      const jwtToken = String(userData.jwt).trim();
      console.log("Processed JWT token:", jwtToken);
      console.log("Processed JWT token length:", jwtToken.length);

      // Clear localStorage trước khi set
      localStorage.removeItem("authToken");
      localStorage.removeItem("userData");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("tokenExpires");
      localStorage.removeItem("userName");

      // Set từng item một cách riêng biệt
      localStorage.setItem("userData", JSON.stringify(userData));
      localStorage.setItem("authToken", jwtToken);
      localStorage.setItem("userName", userData.userName || "");

      if (userData.refreshToken) {
        localStorage.setItem("refreshToken", userData.refreshToken);
      }

      if (userData.dateExpires) {
        localStorage.setItem("tokenExpires", userData.dateExpires);
      }

      // Verify ngay sau khi set
      const verifyToken = localStorage.getItem("authToken");
      const verifyUserData = localStorage.getItem("userData");

      console.log("=== VERIFICATION ===");
      console.log("Saved authToken:", verifyToken);
      console.log("Saved authToken length:", verifyToken?.length);
      console.log("Saved userData:", verifyUserData);
      console.log("Token match:", verifyToken === jwtToken);

      if (verifyToken !== jwtToken) {
        console.error(
          "TOKEN SAVE FAILED! Expected:",
          jwtToken,
          "Got:",
          verifyToken
        );
        throw new Error("Failed to save token to localStorage");
      }
    } catch (error) {
      console.error("Error in setUserData:", error);
      throw error;
    }
  }

  static getUserData(): ResponseLoginWeb | null {
    const userData = localStorage.getItem("userData");
    return userData ? JSON.parse(userData) : null;
  }

  static getToken(): string | null {
    return localStorage.getItem("authToken");
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem("refreshToken");
  }

  static getTokenExpiration(): Date | null {
    const expires = localStorage.getItem("tokenExpires");
    return expires ? new Date(expires) : null;
  }

  static isTokenExpired(): boolean {
    const expirationDate = this.getTokenExpiration();
    if (!expirationDate) {
      console.log("No expiration date found, treating as expired");
      return true;
    }

    const now = new Date();
    const expired = now >= expirationDate;

    console.log("Token expiration check:", {
      expirationDate: expirationDate.toISOString(),
      now: now.toISOString(),
      expired,
    });

    return expired;
  }

  static removeToken(): void {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userData");
    localStorage.removeItem("tokenExpires");
  }

  static isAuthenticated(): boolean {
    const token = this.getToken();
    const userData = this.getUserData();

    console.log("Checking authentication:", {
      token: token,
      tokenExists: !!token,
      userData: !!userData,
      tokenLength: token?.length || 0,
    });

    // Kiểm tra token có tồn tại và không rỗng
    if (!token || token.trim() === "") {
      console.log("No valid token found");
      return false;
    }

    // Kiểm tra userData có tồn tại
    if (!userData) {
      console.log("No userData found");
      return false;
    }

    // Kiểm tra token có hết hạn không
    if (this.isTokenExpired()) {
      console.log("Token expired");
      return false;
    }

    console.log("User is authenticated");
    return true;
  }

  static getTimeUntilExpiration(): number {
    const expirationDate = this.getTokenExpiration();
    if (!expirationDate) {
      return 0;
    }
    return Math.max(0, expirationDate.getTime() - new Date().getTime());
  }

  static shouldRefreshToken(): boolean {
    const timeUntilExpiration = this.getTimeUntilExpiration();
    // Refresh token nếu còn ít hơn 5 phút (300000 ms)
    return timeUntilExpiration > 0 && timeUntilExpiration < 300000;
  }
}
